import { useEffect, useState } from 'react';
import { ErrorBanner, Loading, Modal } from '../components/ui';
import { api } from '../lib/api';
import { formatNumber } from '../lib/format';

interface Preview {
  keptCategories: string[];
  productCategories: { name: string; active: number; kept: boolean }[];
  products: number;
  inactiveProducts: number;
  serviceVendors: { vendor: string; count: number }[];
  inactiveServices: number;
  productsHere: number;
  categoriesToDelete: { Name: string; Ticks: number; Products: number }[];
}
export interface ImportResult {
  products: number;
  services: number;
  added: number;
  updated: number;
  deleted: number;
  retired: number;
  categoriesDeleted: string[];
  matrixTicksRemoved: number;
  skipped: string[];
}

const n = (v: number) => formatNumber(v);

/**
 * Import from Autotask, previewed first (rules in api lib/productImport.ts):
 * the active Backup and Software products, plus the active services of the
 * vendors ticked here — and, said before it happens, what goes: every other
 * product, and every category but Backup and Software with the client
 * product-matrix ticks on them.
 */
export function ProductImportDialog({ onClose, onDone }: { onClose: () => void; onDone: (r: ImportResult) => void }) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vendors, setVendors] = useState<Set<string>>(new Set());
  const [running, setRunning] = useState(false);

  useEffect(() => {
    api
      .get<Preview>('/products/import-autotask/preview')
      .then(setPreview)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not read Autotask'));
  }, []);

  const toggle = (v: string) =>
    setVendors((cur) => {
      const next = new Set(cur);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  const services = preview ? preview.serviceVendors.filter((s) => vendors.has(s.vendor)).reduce((t, s) => t + s.count, 0) : 0;
  const ticks = preview ? preview.categoriesToDelete.reduce((t, c) => t + c.Ticks, 0) : 0;
  const total = (preview?.products ?? 0) + services;

  async function run() {
    setRunning(true);
    setError(null);
    try {
      onDone(await api.post<ImportResult>('/products/import-autotask', { serviceVendors: [...vendors] }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The import failed');
      setRunning(false);
    }
  }

  return (
    <Modal
      title="Import from Autotask"
      onClose={running ? () => undefined : onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={running}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void run()} disabled={!preview || running || total === 0}>
            {running ? 'Importing…' : `Replace the catalogue with ${n(total)} item${total === 1 ? '' : 's'}`}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      {!preview && !error && <Loading label="Reading Autotask's products and services" />}
      {preview && (
        <div className="pi">
          <section>
            <h4>Products — {n(preview.products)} come over</h4>
            <p className="xs muted">
              Active products in {preview.keptCategories.join(' and ')}, with their category. Autotask's other categories are left out
              {preview.inactiveProducts ? `, and so are ${n(preview.inactiveProducts)} inactive products` : ''}.
            </p>
            <ul className="pi-cats">
              {preview.productCategories.map((c) => (
                <li key={c.name} className={c.kept ? 'is-kept' : undefined}>
                  <span>{c.name}</span>
                  <span>
                    {n(c.active)} {c.kept ? '· comes over' : '· left out'}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <div className="row-between">
              <h4>Services — {n(services)} chosen</h4>
              <span className="xs">
                <button type="button" className="link-button" onClick={() => setVendors(new Set(preview.serviceVendors.map((s) => s.vendor)))}>
                  all
                </button>
                {' · '}
                <button type="button" className="link-button" onClick={() => setVendors(new Set())}>
                  none
                </button>
              </span>
            </div>
            <p className="xs muted">
              Active services from the vendors you tick. Autotask gives services no category, so they arrive without one
              {preview.inactiveServices ? `; ${n(preview.inactiveServices)} inactive services are left out` : ''}.
            </p>
            {preview.serviceVendors.length === 0 ? (
              <p className="small muted">Autotask has no active services.</p>
            ) : (
              <ul className="pi-vendors">
                {preview.serviceVendors.map((s) => (
                  <li key={s.vendor}>
                    <label>
                      <input type="checkbox" checked={vendors.has(s.vendor)} onChange={() => toggle(s.vendor)} />
                      <span>{s.vendor}</span>
                      <span className="muted">{n(s.count)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="pi-warn">
            <h4>What this removes</h4>
            <ul>
              <li>
                Every other product in the catalogue (now {n(preview.productsHere)} active). Ones a recommendation, scope of work,
                purchase order or depot asset uses are retired instead of deleted.
              </li>
              {preview.categoriesToDelete.length > 0 && (
                <li>
                  {n(preview.categoriesToDelete.length)} categor{preview.categoriesToDelete.length === 1 ? 'y' : 'ies'} — every one
                  but {preview.keptCategories.join(' and ')} —{' '}
                  {ticks > 0 ? (
                    <strong>
                      and {n(ticks)} client product-matrix tick{ticks === 1 ? '' : 's'} on them in Account Management
                    </strong>
                  ) : (
                    'with no client product-matrix ticks on them'
                  )}
                  .
                  <details className="mt-1">
                    <summary className="xs">Which categories</summary>
                    <p className="xs muted">
                      {preview.categoriesToDelete.map((c) => `${c.Name}${c.Ticks ? ` (${c.Ticks} ticks)` : ''}`).join(' · ')}
                    </p>
                  </details>
                </li>
              )}
            </ul>
          </section>
        </div>
      )}
    </Modal>
  );
}
